import React, { useState, useEffect } from 'react';
import './Suppliers.css';
import supplierService from '../services/supplierService';

const Suppliers = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    address: '',
    tax_number: '',
    status: 'active'
  });

  // Search and Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');

  // Fetch suppliers on component mount
  useEffect(() => {
    fetchSuppliers();
  }, []);

  // Apply filters and search whenever relevant states change
  useEffect(() => {
    applyFiltersAndSearch();
  }, [suppliers, searchTerm, statusFilter, sortBy, sortOrder]);

  const fetchSuppliers = async () => {
    setLoading(true);
    try {
      const response = await supplierService.getAllSuppliers();
      setSuppliers(response.data || []);
      setError('');
    } catch (err) {
      setError('Failed to fetch suppliers');
      console.error('Error fetching suppliers:', err);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let result = [...suppliers];

    // Apply search filter
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      result = result.filter(supplier =>
        supplier.name?.toLowerCase().includes(lowercasedSearch) ||
        supplier.contact_person?.toLowerCase().includes(lowercasedSearch) ||
        supplier.email?.toLowerCase().includes(lowercasedSearch) ||
        supplier.phone?.toLowerCase().includes(lowercasedSearch) ||
        supplier.address?.toLowerCase().includes(lowercasedSearch) ||
        supplier.tax_number?.toLowerCase().includes(lowercasedSearch)
      );
    }

    // Apply status filter
    if (statusFilter !== 'all') {
      result = result.filter(supplier => supplier.status === statusFilter);
    }

    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortBy] || '';
      let bValue = b[sortBy] || '';

      // Handle string comparison
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }

      if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredSuppliers(result);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const handleStatusFilter = (e) => {
    setStatusFilter(e.target.value);
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(column);
      setSortOrder('asc');
    }
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setSortBy('name');
    setSortOrder('asc');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (editingSupplier) {
        await supplierService.updateSupplier(editingSupplier.id, formData);
      } else {
        await supplierService.createSupplier(formData);
      }
      
      setShowModal(false);
      setEditingSupplier(null);
      setFormData({
        name: '',
        contact_person: '',
        email: '',
        phone: '',
        address: '',
        tax_number: '',
        status: 'active'
      });
      fetchSuppliers();
    } catch (err) {
      setError('Failed to save supplier');
      console.error('Error saving supplier:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setFormData({
      name: supplier.name,
      contact_person: supplier.contact_person,
      email: supplier.email,
      phone: supplier.phone,
      address: supplier.address,
      tax_number: supplier.tax_number,
      status: supplier.status
    });
    setShowModal(true);
  };

  const handleDelete = async (supplierId) => {
    if (window.confirm('Are you sure you want to delete this supplier?')) {
      try {
        await supplierService.deleteSupplier(supplierId);
        fetchSuppliers();
      } catch (err) {
        setError('Failed to delete supplier');
        console.error('Error deleting supplier:', err);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      contact_person: '',
      email: '',
      phone: '',
      address: '',
      tax_number: '',
      status: 'active'
    });
    setEditingSupplier(null);
    setShowModal(false);
  };

  const getSortIcon = (column) => {
    if (sortBy !== column) return '↕️';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="suppliers-container">
      <div className="suppliers-header">
        <h1 className="suppliers-title">Suppliers</h1>
        <p className="suppliers-subtitle">Manage your suppliers</p>
        <button 
          className="suppliers-primary-btn"
          onClick={() => setShowModal(true)}
        >
          Add New Supplier
        </button>
      </div>

      {/* Search and Filter Section */}
      <div className="suppliers-controls">
        <div className="suppliers-search-filter">
          <div className="suppliers-search-box">
            <input
              type="text"
              placeholder="Search suppliers..."
              value={searchTerm}
              onChange={handleSearch}
              className="suppliers-search-input"
            />
            <span className="suppliers-search-icon">🔍</span>
          </div>
          
          <div className="suppliers-filter-group">
            <select 
              value={statusFilter} 
              onChange={handleStatusFilter}
              className="suppliers-filter-select"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>

            <select 
              value={sortBy} 
              onChange={(e) => setSortBy(e.target.value)}
              className="suppliers-filter-select"
            >
              <option value="name">Sort by Name</option>
              <option value="contact_person">Sort by Contact Person</option>
              <option value="email">Sort by Email</option>
              <option value="status">Sort by Status</option>
            </select>

            <button 
              onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
              className="suppliers-sort-btn"
            >
              {sortOrder === 'asc' ? '↑ Ascending' : '↓ Descending'}
            </button>

            {(searchTerm || statusFilter !== 'all') && (
              <button 
                onClick={clearFilters}
                className="suppliers-clear-filters-btn"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>

        <div className="suppliers-results-info">
          Showing {filteredSuppliers.length} of {suppliers.length} suppliers
          {searchTerm && (
            <span className="suppliers-search-term">
              for "{searchTerm}"
            </span>
          )}
        </div>
      </div>

      <div className="suppliers-content">
        {error && (
          <div className="suppliers-alert suppliers-alert-error">
            {error}
          </div>
        )}

        {loading && !suppliers.length ? (
          <div className="suppliers-loading">Loading suppliers...</div>
        ) : (
          <div className="suppliers-table-container">
            {filteredSuppliers.length === 0 ? (
              <div className="suppliers-empty-state">
                <div className="suppliers-empty-icon">📭</div>
                <h3>No suppliers found</h3>
                <p>
                  {searchTerm || statusFilter !== 'all' 
                    ? 'Try adjusting your search or filters to find what you\'re looking for.'
                    : 'No suppliers available. Add your first supplier to get started.'
                  }
                </p>
                {(searchTerm || statusFilter !== 'all') && (
                  <button 
                    onClick={clearFilters}
                    className="suppliers-primary-btn"
                  >
                    Clear Filters
                  </button>
                )}
              </div>
            ) : (
              <table className="suppliers-data-table">
                <thead>
                  <tr>
                    <th onClick={() => handleSort('name')} className="suppliers-sortable-header">
                      Name {getSortIcon('name')}
                    </th>
                    <th onClick={() => handleSort('contact_person')} className="suppliers-sortable-header">
                      Contact Person {getSortIcon('contact_person')}
                    </th>
                    <th onClick={() => handleSort('email')} className="suppliers-sortable-header">
                      Email {getSortIcon('email')}
                    </th>
                    <th>Phone</th>
                    <th onClick={() => handleSort('status')} className="suppliers-sortable-header">
                      Status {getSortIcon('status')}
                    </th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSuppliers.map(supplier => (
                    <tr key={supplier.id}>
                      <td>{supplier.name}</td>
                      <td>{supplier.contact_person || 'N/A'}</td>
                      <td>{supplier.email || 'N/A'}</td>
                      <td>{supplier.phone || 'N/A'}</td>
                      <td>
                        <span className={`suppliers-status-badge suppliers-status-${supplier.status}`}>
                          {supplier.status}
                        </span>
                      </td>
                      <td>
                        <button 
                          className="suppliers-btn-sm suppliers-btn-info"
                          onClick={() => handleEdit(supplier)}
                        >
                          Edit
                        </button>
                        <button 
                          className="suppliers-btn-sm suppliers-btn-danger"
                          onClick={() => handleDelete(supplier.id)}
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Add/Edit Supplier Modal */}
        {showModal && (
          <div className="suppliers-modal-overlay">
            <div className="suppliers-modal">
              <div className="suppliers-modal-header">
                <h3>{editingSupplier ? 'Edit Supplier' : 'Add New Supplier'}</h3>
                <button className="suppliers-close-btn" onClick={resetForm}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="suppliers-modal-body">
                  <div className="suppliers-form-group">
                    <label>Supplier Name *</label>
                    <input
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="suppliers-form-group">
                    <label>Contact Person</label>
                    <input
                      type="text"
                      value={formData.contact_person}
                      onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                    />
                  </div>
                  <div className="suppliers-form-group">
                    <label>Email</label>
                    <input
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                    />
                  </div>
                  <div className="suppliers-form-group">
                    <label>Phone</label>
                    <input
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    />
                  </div>
                  <div className="suppliers-form-group">
                    <label>Address</label>
                    <textarea
                      value={formData.address}
                      onChange={(e) => setFormData({...formData, address: e.target.value})}
                      rows="3"
                    />
                  </div>
                  <div className="suppliers-form-group">
                    <label>Tax Number</label>
                    <input
                      type="text"
                      value={formData.tax_number}
                      onChange={(e) => setFormData({...formData, tax_number: e.target.value})}
                    />
                  </div>
                  <div className="suppliers-form-group">
                    <label>Status</label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value})}
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
                <div className="suppliers-modal-footer">
                  <button type="button" className="suppliers-btn-sm suppliers-btn-secondary" onClick={resetForm}>
                    Cancel
                  </button>
                  <button type="submit" className="suppliers-primary-btn" disabled={loading}>
                    {loading ? 'Saving...' : (editingSupplier ? 'Update' : 'Create')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Suppliers;